import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';

// ── helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) => String(n).padStart(2, '0');

const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '–';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${fmt(m)}m` : `${m}m`;
};

const formatTime = (isoString) => {
  if (!isoString) return '–';
  const d = new Date(isoString);
  // Prague = UTC+2 (summer) – good enough approximation
  const pMs = d.getTime() + 2 * 3600 * 1000;
  const pd = new Date(pMs);
  return `${fmt(pd.getUTCHours())}:${fmt(pd.getUTCMinutes())}`;
};

// Build an array of YYYY-MM-DD for the 7-day window ending at `endDate`
const buildWeekDays = (endDate) => {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(endDate);
    d.setDate(d.getDate() - i);
    const key = d.toLocaleDateString('en-CA'); // YYYY-MM-DD
    const label = d.toLocaleDateString('cs-CZ', { weekday: 'short', day: 'numeric', month: 'numeric' });
    days.push({ key, label });
  }
  return days;
};

const colorForMinutes = (min) => {
  if (!min || min <= 0) return '';
  if (min < 120) return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
  if (min < 300) return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
  if (min < 480) return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
  return 'bg-emerald-200 dark:bg-emerald-900/40 text-emerald-900 dark:text-emerald-200 font-semibold';
};

// ── Timeline detail modal ─────────────────────────────────────────────────────

const TimelineModal = ({ entry, onClose }) => {
  if (!entry) return null;

  const { userName, dateKey, firstActivity, lastActivity, workedMinutes, activityCount, timeline } = entry;

  // Hourly buckets 7–21 only
  const HOUR_START = 7;
  const HOUR_END   = 21;
  const HOUR_COUNT = HOUR_END - HOUR_START + 1; // 15 buckets
  const buckets = Array(HOUR_COUNT).fill(0);
  timeline.forEach(minOfDay => {
    const h = Math.floor(minOfDay / 60);
    if (h >= HOUR_START && h <= HOUR_END) buckets[h - HOUR_START]++;
  });

  const maxBucket = Math.max(...buckets, 1);

  // Working window (clamped to display range)
  const startHour = timeline.length ? Math.max(HOUR_START, Math.floor(Math.min(...timeline) / 60)) : HOUR_START;
  const endHour   = timeline.length ? Math.min(HOUR_END,   Math.floor(Math.max(...timeline) / 60) + 1) : HOUR_END;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl p-6"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              ⏱ {userName}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {new Date(dateKey).toLocaleDateString('cs-CZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none">&times;</button>
        </div>

        {/* Summary pills */}
        <div className="flex flex-wrap gap-3 mb-6 text-sm">
          <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full">
            🕐 Začátek: {formatTime(firstActivity)}
          </span>
          <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full">
            🕐 Konec: {formatTime(lastActivity)}
          </span>
          <span className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 px-3 py-1 rounded-full">
            ⏱ Odpracováno: {formatDuration(workedMinutes)}
          </span>
          <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full">
            📝 Aktivit: {activityCount}
          </span>
        </div>

        {/* Hourly bar chart */}
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Aktivita v průběhu dne (počet uložení)
        </p>
        <div className="flex items-end gap-[3px] h-24 border-b border-gray-200 dark:border-gray-600 pb-1">
          {buckets.map((count, i) => {
            const h = HOUR_START + i;
            const inRange = h >= startHour && h <= endHour;
            const heightPct = count > 0 ? Math.max(8, Math.round((count / maxBucket) * 100)) : 0;
            return (
              <div key={h} className="flex-1 flex flex-col items-center justify-end h-full" title={`${fmt(h)}:00 – ${count} aktivit`}>
                <div
                  className={`w-full rounded-t transition-all ${
                    count > 0
                      ? inRange ? 'bg-blue-500 dark:bg-blue-400' : 'bg-gray-300 dark:bg-gray-600'
                      : ''
                  }`}
                  style={{ height: count > 0 ? `${heightPct}%` : '2px', minHeight: count > 0 ? '4px' : undefined }}
                />
              </div>
            );
          })}
        </div>
        {/* Hour labels – every hour 7–21 */}
        <div className="flex gap-[3px] mt-1">
          {buckets.map((_, i) => {
            const h = HOUR_START + i;
            return (
              <div key={h} className="flex-1 text-center text-[9px] text-gray-400 dark:text-gray-500">
                {fmt(h)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Main widget ───────────────────────────────────────────────────────────────

const WorkTimeWidget = ({ currentUserId, userRole, isDarkMode, showAllUsersData = false, onOpenSettings }) => {
  const today = new Date().toLocaleDateString('en-CA');

  const [endDate, setEndDate]     = useState(today);
  const [data, setData]           = useState([]);   // flat array from API
  const [loading, setLoading]     = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);

  const days = buildWeekDays(endDate);
  const from = days[0].key;
  const to   = days[days.length - 1].key;

  const load = useCallback(async () => {
    if (!currentUserId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/work-time-stats?from=${from}&to=${to}&userId=${currentUserId}`);
      if (!res.ok) throw new Error(res.status);
      setData(await res.json());
    } catch (e) {
      console.error('WorkTimeWidget fetch error', e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, currentUserId]);

  useEffect(() => { load(); }, [load]);

  // Build lookup map: "userId|dateKey" → entry
  const map = {};
  data.forEach(e => { map[`${e.userId}|${e.dateKey}`] = e; });

  // Unique users in the period, sorted by total worked desc
  // Non-admin or showAllUsersData=false → only current user
  const usersMap = {};
  data.forEach(e => {
    if (!showAllUsersData && String(e.userId) !== String(currentUserId)) return;
    if (!usersMap[e.userId]) usersMap[e.userId] = { userId: e.userId, userName: e.userName, total: 0 };
    usersMap[e.userId].total += e.workedMinutes || 0;
  });
  const users = Object.values(usersMap).sort((a, b) => b.total - a.total);

  const goBack  = () => {
    const d = new Date(endDate); d.setDate(d.getDate() - 7);
    setEndDate(d.toLocaleDateString('en-CA'));
  };
  const goForward = () => {
    const d = new Date(endDate); d.setDate(d.getDate() + 7);
    const newKey = d.toLocaleDateString('en-CA');
    if (newKey <= today) setEndDate(newKey);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8 relative">
      {/* Gear button – only for admin */}
      {userRole === 'admin' && onOpenSettings && (
        <button
          onClick={onOpenSettings}
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
          title="Nastavení widgetu"
        >
          <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">
          ⏱ Jak dlouho jsem pracoval v CRM?
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={goBack}
            className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm transition-colors"
          >← Předchozí</button>
          <span className="text-sm text-gray-500 dark:text-gray-400 min-w-[180px] text-center">
            {new Date(from).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'short' })} – {new Date(to).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
          <button
            onClick={goForward}
            disabled={endDate >= today}
            className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >Další →</button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent" />
        </div>
      ) : users.length === 0 ? (
        <p className="text-gray-400 dark:text-gray-500 text-center py-6">Žádná data o aktivitách v tomto období.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-200 dark:border-gray-600">
                <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 sticky left-0 bg-white dark:bg-gray-800 min-w-[130px]">
                  Obchodník
                </th>
                {days.map(d => (
                  <th key={d.key} className={`text-center py-2 px-2 font-semibold text-xs min-w-[90px] ${d.key === today ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {d.label}
                  </th>
                ))}
                <th className="text-right py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 min-w-[80px]">
                  Celkem
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, idx) => (
                <tr key={user.userId} className={`border-b border-gray-100 dark:border-gray-700 ${idx % 2 === 0 ? '' : 'bg-gray-50/50 dark:bg-gray-700/20'}`}>
                  <td className="py-2 px-3 font-medium text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {user.userName.charAt(0).toUpperCase()}
                      </div>
                      {user.userName}
                    </div>
                  </td>
                  {days.map(d => {
                    const entry = map[`${user.userId}|${d.key}`];
                    const min = entry?.workedMinutes || 0;
                    return (
                      <td key={d.key} className="py-2 px-2 text-center">
                        {entry ? (
                          <button
                            onClick={() => setDetailEntry(entry)}
                            title={`${formatTime(entry.firstActivity)} – ${formatTime(entry.lastActivity)} | ${entry.activityCount} aktivit\nKlikněte pro detail`}
                            className={`w-full px-2 py-1 rounded-lg text-xs transition-all hover:scale-105 hover:shadow-md ${colorForMinutes(min)}`}
                          >
                            {formatDuration(min)}
                          </button>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600 text-xs">–</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="py-2 px-3 text-right font-semibold text-gray-700 dark:text-gray-300 tabular-nums">
                    {formatDuration(user.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      {users.length > 0 && (
        <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400 mt-3 justify-end flex-wrap">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-200 dark:bg-yellow-900/30" /> &lt; 2h</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-200 dark:bg-blue-900/30" /> 2 – 5h</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 dark:bg-green-900/30" /> 5 – 8h</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-200 dark:bg-emerald-900/40" /> 8h+</span>
          <span className="text-gray-400 dark:text-gray-500 italic">Kliknutím zobrazíte denní detail</span>
        </div>
      )}

      {/* Detail modal */}
      {detailEntry && <TimelineModal entry={detailEntry} onClose={() => setDetailEntry(null)} />}
    </div>
  );
};

export default WorkTimeWidget;
